/* eslint-disable import/prefer-default-export */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import QueryKeys from '../config';
import API from '../../../network';
import { InterviewPositionType } from './types';

type UseMeetInterviewPositionsProps = {
  enabled?: boolean;
  jobOrderId?: number;
  jobId?: string;
  jobTitle?: string;
};

const fetchMeetInterviewPositions = async (params: {
  jobOrderId?: number;
  jobId?: string;
  jobTitle?: string;
}): Promise<InterviewPositionType[]> => {
  try {
    const response = await API.getInstance().getMeetInterviewPositions(params);
    const meetPositions = response.data ?? [];
    if (meetPositions.length > 0) {
      return meetPositions;
    }
  } catch {
    // Meet API unavailable (e.g. cert / 500) — fall back to legacy list
  }

  const legacy = await API.getInstance().getInterviewPositions();
  return legacy.data ?? [];
};

const useMeetInterviewPositions = (props?: UseMeetInterviewPositionsProps) => {
  const {
    enabled = true,
    jobOrderId,
    jobId,
    jobTitle,
  } = props || {};

  const canFetch = Boolean(enabled && (jobOrderId || jobId));

  const {
    data, error, refetch, isFetching,
  } = useQuery({
    queryKey: [QueryKeys.INTERVIEWPOSITIONS, 'meet', jobOrderId, jobId, jobTitle],
    queryFn: () => fetchMeetInterviewPositions({ jobOrderId, jobId, jobTitle }),
    refetchOnWindowFocus: false,
    initialData: [],
    enabled: canFetch,
  });

  const interviewPositionsOptions = React.useMemo(
    () => data?.map((option) => ({
      label: `${option.interviewID} - ${option.positionTitle}`,
      value: option.interviewPositionId,
    })) || [],
    [data],
  );

  return {
    interviewPositionsLoading: isFetching,
    interviewPositionsOptions,
    data,
    error,
    fetchInterviewPositions: refetch,
  };
};

export {
  useMeetInterviewPositions,
};
